import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DollarSign } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-[linear-gradient(135deg,_hsl(189_70%_40%)_0%,_hsl(189_80%_30%)_100%)]">
        <div className="max-w-md text-center text-white">
          <div className="w-20 h-20 mx-auto mb-8 bg-white/20 rounded-2xl flex items-center justify-center">
            <DollarSign size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            TavJournals
          </h1>
          <p className="text-lg text-white/80">
            Professional-grade accounting made simple. Track your finances with clarity and confidence.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex items-center justify-center p-6 bg-[hsl(var(--surface-2))]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="lg:hidden w-12 h-12 mx-auto mb-4 bg-[hsl(var(--brand-500))] rounded-xl flex items-center justify-center">
              <DollarSign size={24} className="text-white" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk' }}>
              Welcome back
            </CardTitle>
            <CardDescription>
              Sign in to your account to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-[hsl(var(--muted-ink))]">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-[hsl(var(--brand-600))] hover:underline font-medium">
                Create one
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
